import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import AuthController from "../../controllers/AuthController";

const AuthContext = React.createContext();

//#region Helper methods
function handleServerError(error) {
  if (error.response) {
    // The request was made and the server responded with an status code that falls out of the range of 2xx
  
    if (
      error.response.status === 401 &&
      error.response.data.code === "BAD-USER-PASSWORD"
    ) {
      throw new Error("accessError");
    }
    if (
      error.response.status === 401 &&
      error.response.data.code === "EMAIL-NOT-VALIDATED"
    ) {
      throw new Error("EMAIL-NOT_VALIDATED");
    }
    if (
      error.response.status === 401 &&
      error.response.data.msg === "Token has expired"
    )
      AuthProvider.signOut();
    if (error.response.status === 500) throw new Error("serverError");
    throw new Error("accessError");
  } else if (error.request) {
     throw new Error("connectionError");
  } else {
    if (error?.message) throw new Error(error.message);
    else throw new Error("unknownError");
  }
}

function AuthProvider(props) {
  let [user, setUser] = React.useState({});
  async function signIn(input) {

    try {
      const response = await AuthController.signIn(input);
      if (response.respuesta !== "0") throw new Error("connectionError");

      const { token } = response;
      setUser({
        id: input.typeDoc,
        token,
      });

      return {
        ok: true,
        token,
      };
    } catch (error) {

      handleServerError(error);
    }
  }

  async function signOut() {      
    return setUser(null);
  }

  async function register(input) {
    try {
      const response = await AuthController.register(input);
      if (response?.data.result.ok) {
        return {
          ok: true,
        };
      } else {
        let error = "registerError";
        if (response.data.result.errors?.email) {
          if (
            response.data.result.errors.email.error_code ===
            "OBJECT-ALREADY-EXISTS"
          )
            error = "userAlreadyExistsError";
        }
        return {
          ok: false,
          error,
        };
      }
    } catch (error) {
      handleServerError(error);
    }
  }

  async function forgotPassword(input) {
    try {
      const response = await AuthController.forgotPassword(input);
      if (response?.data.result.ok === true) {
        return {
          ok: true,
        };
      }
      if (response?.data.result.ok === false) {
        return {
          ok: false,
        };
      }
    } catch (error) {
      handleServerError(error);
    }
  }

  async function resetPassword(input) {
    try {
      const response = await AuthController.resetPassword(input);
      // TODO: Clean this when we get an error code from the backend
      if (
        response.error?.response?.data?.errors[0].message ===
        "Signature has expired"
      )
        return { ok: false, error: "expiredResetTokenError" };
      if (response?.data.result.ok === true) {
        // The backend will send an email with instructions
        return {
          ok: true,
        };
      }

      if (response?.data.result.ok === false) {
        return {
          ok: false,
        };
      }
    } catch (error) {
      handleServerError(error);
    }
  }

  async function sendActivation(input) {
    try {
      const response = await AuthController.sendActivation(input);
      if (response?.data.result.ok === true) {
        return {
          ok: true,
        };
      }
      if (response?.data.result.ok === false) {
        return {
          ok: false,
        };
      }
      throw new Error("sendActivationError");
    } catch (e) {
      handleServerError(e);
    }
  }

  const value = {
    user,
    setUser,
    signIn,
    signOut,
    register,
    forgotPassword,
    resetPassword,
    sendActivation,
  };

  return <AuthContext.Provider value={value} {...props} />;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// TODO: Check these Require user information workflows. Not fully tested

function RequireAuth({ children }) {
  let location = useLocation();
  let { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user.is_admin === false && user.is_personal_study === false) {
    return <Navigate to="/personal-study-introduction" />;
  }
  return children;
}

function NotRequireAuth({ children }) {
  let location = useLocation();
  let { user } = useAuth();
  if (user !== null) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
}

export { AuthProvider, useAuth, RequireAuth, NotRequireAuth };
